/** Admin voice settings — pick the assistant's ElevenLabs voice or clone a branded one. */
import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { SpeakerHigh, StopCircle, CircleNotch, CheckCircle, Waveform, UploadSimple } from "@phosphor-icons/react";

const CATEGORY_TONE = {
  cloned: "border-purple-300 bg-purple-50 text-purple-700",
  professional: "border-purple-300 bg-purple-50 text-purple-700",
  premade: "border-gray-200 bg-gray-50 text-gray-500",
};

export const VoiceSettings = () => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [preview, setPreview] = useState({ id: null, state: "" });
  const [selecting, setSelecting] = useState("");
  const [cloneName, setCloneName] = useState("Vaku Voice");
  const [cloneFiles, setCloneFiles] = useState([]);
  const [cloning, setCloning] = useState(false);
  const audioRef = useRef(null);
  const fileRef = useRef(null);

  const load = () => api.get("/voice/voices")
    .then((r) => { setData(r.data); setErr(""); })
    .catch((e) => setErr(e?.response?.data?.detail || "Could not load voices"));
  useEffect(() => { load(); return () => audioRef.current?.pause(); }, []);

  const playPreview = async (v) => {
    if (preview.id === v.voice_id && preview.state === "playing") {
      audioRef.current?.pause();
      setPreview({ id: null, state: "" });
      return;
    }
    audioRef.current?.pause();
    setPreview({ id: v.voice_id, state: "loading" });
    try {
      const r = await api.post("/voice/preview", { voice_id: v.voice_id }, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = new Audio(url);
      audioRef.current = a;
      a.onended = () => { setPreview({ id: null, state: "" }); URL.revokeObjectURL(url); };
      await a.play();
      setPreview({ id: v.voice_id, state: "playing" });
    } catch {
      toast.error("Preview failed");
      setPreview({ id: null, state: "" });
    }
  };

  const select = async (v) => {
    setSelecting(v.voice_id);
    try {
      await api.post("/voice/select", { voice_id: v.voice_id, name: v.name });
      setData((d) => ({ ...d, selected: v.voice_id }));
      toast.success(`Assistant voice set to ${v.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not set voice");
    } finally { setSelecting(""); }
  };

  const clone = async () => {
    if (!cloneName.trim()) { toast.error("Give your voice a name"); return; }
    if (!cloneFiles.length) { toast.error("Upload at least one audio sample"); return; }
    setCloning(true);
    try {
      const fd = new FormData();
      fd.append("name", cloneName.trim());
      cloneFiles.forEach((f) => fd.append("files", f));
      const r = await api.post("/voice/clone", fd);
      toast.success(`Voice "${r.data.name}" created — select it below`);
      setCloneFiles([]);
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cloning failed");
    } finally { setCloning(false); }
  };

  return (
    <section className="border border-gray-200 bg-white" data-testid="voice-settings-section">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Assistant Voice</div>
        <h3 className="text-xl font-black tracking-tight mt-0.5 flex items-center gap-2">
          <Waveform size={18} weight="fill" className="text-[#0020C2]" /> ElevenLabs voice picker &amp; cloning
        </h3>
        <p className="text-[11px] text-gray-500 mt-1">
          The voice used when AI assistant replies are read aloud (Planning &amp; Commodity AI). Preview any voice, pick one, or clone your own for a branded assistant.
        </p>
      </div>

      {err && <div className="m-6 text-xs text-[#DC2626] border border-[#DC2626] bg-red-50 px-3 py-2" data-testid="voice-error">{err}</div>}
      {!data && !err && <div className="p-6 text-sm text-gray-500">Loading voices…</div>}

      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr]">
          <div className="p-6 lg:border-r border-gray-200">
            <div className="text-[11px] font-bold uppercase tracking-wider text-gray-600 mb-2 flex items-center justify-between">
              <span>Voice library <span className="font-normal normal-case text-gray-400">({data.voices.length} voices)</span></span>
              <span className="font-normal normal-case text-gray-500" data-testid="voice-active-label">
                Active: <b className="text-emerald-700">
                  {data.voices.find((v) => v.voice_id === data.selected)?.name || "Rachel (default)"}
                </b>
              </span>
            </div>
            <div className="max-h-[340px] overflow-y-auto border border-gray-200 divide-y divide-gray-100" data-testid="voice-list">
              {data.voices.map((v) => {
                const active = data.selected === v.voice_id;
                return (
                  <div key={v.voice_id} data-testid={`voice-row-${v.voice_id}`}
                    className={`flex items-center gap-3 px-4 py-2.5 ${active ? "bg-blue-50/60" : "hover:bg-gray-50"}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-gray-900 truncate">{v.name}</div>
                      <div className="text-[10px] font-mono text-gray-400 truncate">{v.voice_id}</div>
                    </div>
                    {v.category && (
                      <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 border ${CATEGORY_TONE[v.category] || CATEGORY_TONE.premade}`}>
                        {v.category}
                      </span>
                    )}
                    <button onClick={() => playPreview(v)} data-testid={`voice-preview-${v.voice_id}`}
                      title="Preview" className="text-gray-400 hover:text-[#0020C2]">
                      {preview.id === v.voice_id && preview.state === "loading"
                        ? <CircleNotch size={16} className="animate-spin text-[#0020C2]" />
                        : preview.id === v.voice_id && preview.state === "playing"
                          ? <StopCircle size={16} weight="fill" className="text-[#0020C2]" />
                          : <SpeakerHigh size={16} weight="bold" />}
                    </button>
                    {active ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700"
                        data-testid="voice-active-badge">
                        <CheckCircle size={12} weight="fill" /> Active
                      </span>
                    ) : (
                      <button onClick={() => select(v)} disabled={!!selecting} data-testid={`voice-select-${v.voice_id}`}
                        className="text-[10px] font-bold uppercase tracking-wider text-[#0020C2] border border-[#0020C2] px-2 py-1 hover:bg-[#0020C2] hover:text-white disabled:opacity-50">
                        {selecting === v.voice_id ? "…" : "Use"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="p-6 border-t lg:border-t-0 border-gray-200">
            <div className="text-[11px] font-bold uppercase tracking-wider text-gray-600 mb-1">Clone your own voice</div>
            <p className="text-[11px] text-gray-500 leading-snug mb-3">
              Upload 1–3 clean speech recordings (30 seconds to 3 minutes total, no background noise) and ElevenLabs
              creates an instant clone — perfect for a branded &ldquo;Vaku&rdquo; assistant.
            </p>
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 block mb-1">Voice name</label>
            <input value={cloneName} onChange={(e) => setCloneName(e.target.value)} data-testid="clone-name-input"
              className="w-full border border-gray-300 px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-[#0020C2]" />
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 block mb-1">Audio samples</label>
            <input ref={fileRef} type="file" accept="audio/*" multiple data-testid="clone-files-input"
              onChange={(e) => setCloneFiles(Array.from(e.target.files || []))}
              className="w-full text-xs text-gray-600 file:mr-3 file:border file:border-gray-300 file:bg-white file:px-3 file:py-1.5 file:text-[11px] file:font-bold file:uppercase file:tracking-wider file:cursor-pointer mb-1" />
            {cloneFiles.length > 0 && (
              <div className="text-[11px] text-gray-500 mb-2" data-testid="clone-files-count">
                {cloneFiles.length} file{cloneFiles.length > 1 ? "s" : ""} · {(cloneFiles.reduce((s, f) => s + f.size, 0) / 1024 / 1024).toFixed(1)} MB
              </div>
            )}
            <button onClick={clone} disabled={cloning} data-testid="clone-create-btn"
              className="mt-2 inline-flex items-center gap-2 bg-[#0020C2] text-white px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-[#001AA3] disabled:opacity-50">
              {cloning ? <CircleNotch size={13} className="animate-spin" /> : <UploadSimple size={13} weight="bold" />}
              {cloning ? "Cloning…" : "Create voice clone"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
};
